import { FormEvent, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { updateDeal, UpdateDealRequest } from "../../../../../../../service/fund";
import { getDeal, GetDealResponse } from "../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import { deleteWorkflow } from "../../../../../../../service/workflow";
import { useAuth } from "../../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../../utils";
import { getWorkflowTemplatesRoute } from "../../../../../../../utils/routes";
import { Toast } from "../../../../../../../widget";
import { TasksBoardContext } from "../../TasksBoardContext";

interface UseRemoveWorkflowModalProps {
  closeModal: () => void;
}

interface UseRemoveWorkflowModalReturnData {
  handleRemoveWorkflow: (e: FormEvent<HTMLFormElement>) => void;
  isHandleRemoveWorkflowLoading: boolean;
  onCancel: () => void;
  errors: ServiceError[];
}

const getDealCleanupFailureMessage = (workflowUuid: string, dealUuid?: string): string =>
  `Workflow ${workflowUuid} was removed, but deal ${dealUuid ?? "unknown"} could not be updated. Please clear the deal stage data manually.`;

const navigateAfterWorkflowRemoval = (
  navigate: ReturnType<typeof useNavigate>,
  currentUserType: ReturnType<typeof useAuth>["currentUserType"],
  onCancel: () => void
): void => {
  navigate(getWorkflowTemplatesRoute(currentUserType));
  onCancel();
};

const buildUpdateDealRequest = (deal: GetDealResponse): UpdateDealRequest => {
  return {
    conversionProbability: deal.conversionProbability,
    dealDates: {},
    dealSpecificData: deal.dealSpecificData as unknown as UpdateDealRequest["dealSpecificData"],
    dealTeam: deal.usersInvolved?.map((user) => user.uuid) ?? [],
    dealUuid: deal.uuid,
    description: deal.description ?? "",
    estimatedFees: deal.estimatedFees ?? 0,
    introductionDate: deal.introductionDate ?? null,
    IsTemplateSelected: false,
    leadUserUuid: deal.leadUser?.uuid ?? "",
    name: deal.name,
    projectedDpi: deal.projectedDpi,
    relatedObjects:
      deal.relatedObjects?.map((relatedObject) => ({
        objectType: relatedObject.objectType,
        objectUuid: relatedObject.objectUuid,
      })) ?? [],
    rowVersion: deal.rowVersion,
    source: deal.source ?? null,
    stage: "No workflow applied",
    status: deal.status,
    targetCloseDate: deal.targetCloseDate ?? null,
  };
};

export const useRemoveWorkflowModal = ({
  closeModal,
}: UseRemoveWorkflowModalProps): UseRemoveWorkflowModalReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();
  const { deal, isDealWorkflow, tasksBoard } = useContext(TasksBoardContext);
  const [isHandleRemoveWorkflowLoading, setIsHandleRemoveWorkflowLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>([]);

  const onCancel = (): void => {
    setErrors([]);
    closeModal();
  };

  const handleRemoveWorkflow = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const workflow = { uuid: tasksBoard?.results[0].uuid, rowVersion: tasksBoard?.results[0].rowVersion };

    if (!workflow.uuid || !workflow.rowVersion) {
      setErrors([
        {
          code: "NO_WORKFLOW",
          message: "A workflow must be selected",
        },
      ]);
    } else {
      const response = await deleteWorkflow({
        workflowUuid: workflow.uuid,
        workflowRowVersion: workflow.rowVersion,
      });
      if (response.status === Status.Success) {
        if (isDealWorkflow) {
          const dealUuid = tasksBoard?.results[0].dealUuid ?? deal?.uuid;
          const cleanupFailureMessage = getDealCleanupFailureMessage(workflow.uuid, dealUuid);

          if (!dealUuid) {
            setErrors([
              {
                code: "DEAL_UPDATE_AFTER_WORKFLOW_DELETE_FAILED",
                message: cleanupFailureMessage,
              },
            ]);
            Toast.warning({
              message: cleanupFailureMessage,
            });
            navigateAfterWorkflowRemoval(navigate, currentUserType, onCancel);
            return;
          }

          const dealResponse = await getDeal({ dealUuid });

          if (dealResponse.status !== Status.Success || !dealResponse.data) {
            setErrors([
              ...(dealResponse.errors ?? []),
              {
                code: "DEAL_UPDATE_AFTER_WORKFLOW_DELETE_FAILED",
                message: cleanupFailureMessage,
              },
            ]);
            Toast.warning({
              message: cleanupFailureMessage,
            });
            navigateAfterWorkflowRemoval(navigate, currentUserType, onCancel);
            return;
          }

          const updateDealResponse = await updateDeal(buildUpdateDealRequest(dealResponse.data));

          if (updateDealResponse.status !== Status.Success) {
            setErrors([
              ...(updateDealResponse.errors ?? []),
              {
                code: "DEAL_UPDATE_AFTER_WORKFLOW_DELETE_FAILED",
                message: cleanupFailureMessage,
              },
            ]);
            Toast.warning({
              message: cleanupFailureMessage,
            });
            navigateAfterWorkflowRemoval(navigate, currentUserType, onCancel);
            return;
          }
        }

        Toast.success({ message: "Workflow successfully removed" });
        navigateAfterWorkflowRemoval(navigate, currentUserType, onCancel);
      }
      if (response.status === Status.Error && response.errors) {
        setErrors(response.errors);
      }
    }
  }, setIsHandleRemoveWorkflowLoading);

  return { isHandleRemoveWorkflowLoading, handleRemoveWorkflow, onCancel, errors };
};
